import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { ArrowLeft, PanelLeftClose, PanelLeft, ChevronDown, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { formatRoleLabel } from '../lib/roleLabel'
import { getDisplayName } from '../lib/profileDisplayName'
import {
  DASHBOARD_HOME_NAV,
  DASHBOARD_AGENTS_NAV,
  DASHBOARD_KNOWLEDGE_SEARCH_NAV,
  DASHBOARD_MAIN_NAV,
  DASHBOARD_INTERNAL_APPS_NAV,
  DASHBOARD_KNOWLEDGE_NAV,
  DASHBOARD_DEV_TOOLS_NAV,
  DASHBOARD_MCP_NAV,
  DASHBOARD_BASECAMP_NAV,
  DASHBOARD_AI_NAV,
  DASHBOARD_OUTLOOK_NAV,
  DASHBOARD_SHAREPOINT_NAV,
  DASHBOARD_ONENOTE_NAV,
  DASHBOARD_DOCUMENTATION_NAV,
  DASHBOARD_ADMIN_NAV,
  resolveAdminNavPageKey,
} from '../lib/dashboardNav'
import UserAccountMenu, { USER_MENU_CLOSE_MS } from './UserAccountMenu'

export {
  DASHBOARD_HOME_NAV,
  DASHBOARD_AGENTS_NAV,
  DASHBOARD_KNOWLEDGE_SEARCH_NAV,
  DASHBOARD_MAIN_NAV,
  DASHBOARD_INTERNAL_APPS_NAV,
  DASHBOARD_KNOWLEDGE_NAV,
  DASHBOARD_MCP_NAV,
  DASHBOARD_BASECAMP_NAV,
  DASHBOARD_AI_NAV,
  DASHBOARD_OUTLOOK_NAV,
  DASHBOARD_SHAREPOINT_NAV,
  DASHBOARD_ONENOTE_NAV,
  DASHBOARD_DOCUMENTATION_NAV,
  DASHBOARD_ADMIN_NAV,
} from '../lib/dashboardNav'

const SIDEBAR_COLLAPSED_KEY = 'smg-sidebar-collapsed'
const SIDEBAR_WIDTH_EXPANDED = '240px'
const SIDEBAR_WIDTH_COLLAPSED = 'var(--sidebar-width-collapsed)'

function readCollapsedPreference() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

function applyRootSidebarWidth(collapsed) {
  document.documentElement.style.setProperty(
    '--sidebar-width',
    collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED
  )
}

function userInitials(profile) {
  const name = getDisplayName(profile)
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return name.slice(0, 2).toUpperCase()
  }
  const email = profile?.email || ''
  return email.slice(0, 2).toUpperCase() || '?'
}

const sectionLabelStyle = {
  fontSize: '10px',
  fontWeight: '600',
  color: 'var(--color-sidebar-text)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  padding: '16px 12px 6px',
}

const firstSectionLabelStyle = {
  ...sectionLabelStyle,
  padding: '8px 12px 6px',
}

const navBetaBadgeStyle = {
  display: 'inline-block',
  padding: '1px 5px',
  borderRadius: '999px',
  fontSize: '8px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  color: '#92400e',
  backgroundColor: 'rgba(251, 191, 36, 0.25)',
  verticalAlign: 'middle',
}

const ADMIN_COLLAPSE_KEY = 'smg-admin-nav-collapsed'

function readAdminSectionCollapsed() {
  try {
    const raw = localStorage.getItem(ADMIN_COLLAPSE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

/**
 * @param {Object} props
 * @param {'main' | 'admin'} props.variant
 * @param {string} props.activeItemId
 * @param {(id: string) => void} props.onItemClick
 * @param {boolean} props.showQuickAddFooter
 * @param {React.ReactNode} props.quickAddFooter
 * @param {boolean} [props.showAdminPanelMenuItem]
 * @param {() => void} [props.onAdminPanel]
 */
export default function DashboardSidebar({
  variant = 'main',
  activeItemId,
  onItemClick,
  showQuickAddFooter,
  quickAddFooter,
  showAdminPanelMenuItem = false,
  onAdminPanel,
}) {
  const { profile, canAccessPage, canAccessKnowledgeSearch, signOut, isSuperAdmin } = useAuth()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(readCollapsedPreference)
  const [adminSectionsCollapsed, setAdminSectionsCollapsed] = useState(readAdminSectionCollapsed)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuCloseTimerRef = useRef(null)

  useLayoutEffect(() => {
    applyRootSidebarWidth(collapsed)
  }, [collapsed])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const clearUserMenuCloseTimer = useCallback(() => {
    if (userMenuCloseTimerRef.current != null) {
      clearTimeout(userMenuCloseTimerRef.current)
      userMenuCloseTimerRef.current = null
    }
  }, [])

  const openUserMenu = useCallback(() => {
    clearUserMenuCloseTimer()
    setUserMenuOpen(true)
  }, [clearUserMenuCloseTimer])

  const scheduleCloseUserMenu = useCallback(() => {
    clearUserMenuCloseTimer()
    userMenuCloseTimerRef.current = window.setTimeout(() => {
      userMenuCloseTimerRef.current = null
      setUserMenuOpen(false)
    }, USER_MENU_CLOSE_MS)
  }, [clearUserMenuCloseTimer])

  useEffect(() => () => clearUserMenuCloseTimer(), [clearUserMenuCloseTimer])

  const visibleHomeNav = DASHBOARD_HOME_NAV.filter((n) => canAccessPage(n.id))
  const visibleAgentsNav = DASHBOARD_AGENTS_NAV.filter(() => canAccessPage('ai-agents'))
  const visibleKnowledgeSearchNav = DASHBOARD_KNOWLEDGE_SEARCH_NAV.filter(() =>
    canAccessKnowledgeSearch()
  )
  const visibleMainNav = DASHBOARD_MAIN_NAV.filter((n) => !n.hidden && canAccessPage(n.id))
  const visibleInternalAppsNav = DASHBOARD_INTERNAL_APPS_NAV.filter((n) => canAccessPage(n.id))
  const visibleKnowledgeNav = DASHBOARD_KNOWLEDGE_NAV.filter((n) => canAccessPage(resolveAdminNavPageKey(n.id)))
  const visibleDevToolsNav = DASHBOARD_DEV_TOOLS_NAV.filter((n) => canAccessPage(resolveAdminNavPageKey(n.id)))
  const visibleMcpNav = DASHBOARD_MCP_NAV.filter((n) => canAccessPage(n.id))
  const visibleBasecampNav = DASHBOARD_BASECAMP_NAV.filter((n) => canAccessPage(n.id))
  const visibleDocumentationNav = DASHBOARD_DOCUMENTATION_NAV.filter((n) => canAccessPage(n.id))
  const visibleAiNav = DASHBOARD_AI_NAV.filter((n) => {
    if (n.superAdminOnly && !isSuperAdmin) return false
    return canAccessPage(n.id)
  })
  const visibleOutlookNav = DASHBOARD_OUTLOOK_NAV.filter((n) => canAccessPage(n.id))
  const visibleSharepointNav = DASHBOARD_SHAREPOINT_NAV.filter((n) => canAccessPage(n.id))
  const visibleOnenoteNav = DASHBOARD_ONENOTE_NAV.filter((n) => canAccessPage(n.id))
  const visibleAdminNav = DASHBOARD_ADMIN_NAV.filter((n) => canAccessPage(n.id))
  const sidebarDisplayName = getDisplayName(profile)
  const initials = userInitials(profile)

  const handleLogout = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  const navButton = (id, label, Icon, isActive, href, showBeta = false) => {
    const displayLabel = showBeta ? `${label} (Beta)` : label
    return (
    <button
      key={id}
      type="button"
      title={displayLabel}
      aria-label={displayLabel}
      onClick={() => (href ? window.open(href, '_blank', 'noopener,noreferrer') : onItemClick(id))}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : '10px',
        width: '100%',
        padding: collapsed ? '10px 8px' : '9px 12px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: isActive ? 'var(--color-sidebar-active)' : 'transparent',
        color: isActive ? 'var(--color-sidebar-text-active)' : 'var(--color-sidebar-text)',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: isActive ? '600' : '400',
        textAlign: 'left',
        marginBottom: '2px',
        transition: 'background-color 0.15s, color 0.15s',
        borderLeft: isActive ? '3px solid var(--color-accent)' : '3px solid transparent',
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover)'
          e.currentTarget.style.color = 'var(--color-sidebar-text-active)'
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'transparent'
          e.currentTarget.style.color = 'var(--color-sidebar-text)'
        }
      }}
    >
      <Icon size={16} strokeWidth={isActive ? 2.5 : 1.8} />
      {!collapsed && (
        <span style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {label}
          {showBeta && <span style={navBetaBadgeStyle}>Beta</span>}
        </span>
      )}
      {!collapsed && isActive && (
        <div
          style={{
            width: '6px',
            height: '6px',
            backgroundColor: 'var(--color-accent)',
            borderRadius: '50%',
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        />
      )}
    </button>
    )
  }

  const renderNavSection = (title, items, isFirstSection = false) => {
    if (items.length === 0) return null
    return (
      <>
        {!collapsed && (
          <div style={isFirstSection ? firstSectionLabelStyle : sectionLabelStyle}>{title}</div>
        )}
        {items.map(({ id, label, Icon, href, beta }) => {
          const isActive = !href && activeItemId === id
          return navButton(id, label, Icon, isActive, href, beta)
        })}
      </>
    )
  }

  const toggleAdminSection = useCallback((sectionKey) => {
    setAdminSectionsCollapsed((prev) => {
      const next = { ...prev, [sectionKey]: !prev[sectionKey] }
      try {
        localStorage.setItem(ADMIN_COLLAPSE_KEY, JSON.stringify(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const renderCollapsibleAdminSection = (sectionKey, title, items, isFirstSection = false, showBeta = false) => {
    if (items.length === 0) return null
    const isSectionCollapsed = !!adminSectionsCollapsed[sectionKey]
    const hasActiveChild = items.some(({ id, href }) => !href && activeItemId === id)

    return (
      <>
        {!collapsed ? (
          <button
            type="button"
            className="admin-sidebar-section-toggle"
            style={isFirstSection ? { ...firstSectionLabelStyle, display: 'flex', width: '100%' } : undefined}
            onClick={() => toggleAdminSection(sectionKey)}
            aria-expanded={!isSectionCollapsed}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {title}
              {showBeta ? <span className="admin-nav-beta-badge">Beta</span> : null}
            </span>
            {isSectionCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button>
        ) : null}
        {(!isSectionCollapsed || hasActiveChild || collapsed) &&
          items.map(({ id, label, Icon, href, beta }) => {
            const isActive = !href && activeItemId === id
            return navButton(id, label, Icon, isActive, href, beta)
          })}
      </>
    )
  }

  const collapseToggle = (
    <button
      type="button"
      onClick={toggleCollapsed}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: collapsed ? '100%' : '32px',
        height: '32px',
        marginTop: '12px',
        marginLeft: collapsed ? 0 : 'auto',
        borderRadius: '6px',
        border: 'none',
        background: 'rgba(255,255,255,0.06)',
        color: 'var(--color-sidebar-text)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover)'
        e.currentTarget.style.color = 'var(--color-sidebar-text-active)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'
        e.currentTarget.style.color = 'var(--color-sidebar-text)'
      }}
    >
      {collapsed ? <PanelLeft size={18} strokeWidth={2} /> : <PanelLeftClose size={18} strokeWidth={2} />}
    </button>
  )

  return (
    <div
      style={{
        width: 'var(--sidebar-width)',
        minWidth: 'var(--sidebar-width)',
        backgroundColor: 'var(--color-sidebar-bg)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 100,
        borderRight: '1px solid rgba(255,255,255,0.05)',
        transition: 'width 0.2s ease, min-width 0.2s ease',
      }}
    >
      <div
        style={{
          padding: collapsed ? '16px 10px 12px' : '24px 20px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {variant === 'admin' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: collapsed ? 'center' : 'stretch' }}>
            <button
              type="button"
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
              onClick={() => navigate('/')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: collapsed ? 0 : '10px',
                width: '100%',
                padding: collapsed ? '10px 8px' : '8px 10px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--color-sidebar-text-active)',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <ArrowLeft size={18} strokeWidth={2} />
              {!collapsed && <span>Back to Dashboard</span>}
            </button>
            {collapseToggle}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: collapsed ? 'center' : 'stretch' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: '10px',
                width: '100%',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: 'var(--color-accent)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <img
                  src="/logo.jpg"
                  alt="SMG"
                  style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }}
                />
              </div>
              {!collapsed && (
                <div>
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: 'var(--color-sidebar-logo-text)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    SMG
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: 'var(--color-sidebar-text)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Smart Media Group
                  </div>
                </div>
              )}
            </div>
            {collapseToggle}
          </div>
        )}
      </div>

      <nav
        className="dashboard-sidebar-nav"
        style={{ flex: 1, minHeight: 0, padding: collapsed ? '12px 6px' : '12px 8px', overflow: 'auto' }}
      >
        {variant === 'main' ? (
          <>
            {visibleHomeNav.length > 0 ? (
              <>
                {visibleHomeNav.map(({ id, label, Icon, href, beta }) => {
                  const isActive = !href && activeItemId === id
                  return navButton(id, label, Icon, isActive, href, beta)
                })}
                <div
                  style={{
                    height: 1,
                    background: 'rgba(255,255,255,0.08)',
                    margin: collapsed ? '10px 4px 12px' : '10px 12px 12px',
                  }}
                />
              </>
            ) : null}
            {visibleAgentsNav.map(({ id, label, Icon, href, beta }) => {
              const isActive = !href && activeItemId === id
              return navButton(id, label, Icon, isActive, href, beta)
            })}
            {visibleAgentsNav.length > 0 ? (
              <div
                style={{
                  height: 1,
                  background: 'rgba(255,255,255,0.08)',
                  margin: collapsed ? '10px 4px 12px' : '10px 12px 12px',
                }}
              />
            ) : null}
            {visibleKnowledgeSearchNav.map(({ id, label, Icon, href, beta }) => {
              const isActive = !href && activeItemId === id
              return navButton(id, label, Icon, isActive, href, beta)
            })}
            {visibleKnowledgeSearchNav.length > 0 ? (
              <div
                style={{
                  height: 1,
                  background: 'rgba(255,255,255,0.08)',
                  margin: collapsed ? '10px 4px 12px' : '10px 12px 12px',
                }}
              />
            ) : null}
            {renderNavSection('Main', visibleMainNav, visibleHomeNav.length === 0)}
            {renderNavSection('Internal Apps', visibleInternalAppsNav)}
          </>
        ) : (
          <>
            {renderCollapsibleAdminSection('knowledge', 'Knowledge Base', visibleKnowledgeNav, true)}
            {renderCollapsibleAdminSection('devTools', 'Developer Tools', visibleDevToolsNav)}
            {renderCollapsibleAdminSection('mcp', 'MCP Server', visibleMcpNav, false, true)}
            {renderNavSection('Basecamp', visibleBasecampNav)}
            {renderNavSection('AI', visibleAiNav)}
            {renderNavSection('Outlook', visibleOutlookNav)}
            {renderNavSection('SharePoint', visibleSharepointNav)}
            {renderNavSection('OneNote', visibleOnenoteNav)}
            {renderNavSection('Administration', visibleAdminNav)}
            {renderNavSection('Documentation', visibleDocumentationNav)}
          </>
        )}
      </nav>

      <div style={{ padding: collapsed ? '12px 6px' : '12px 8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {showQuickAddFooter && !collapsed && quickAddFooter}
        <div
          style={{ position: 'relative' }}
          onMouseEnter={openUserMenu}
          onMouseLeave={scheduleCloseUserMenu}
        >
          <button
            type="button"
            title={sidebarDisplayName || profile?.email || 'Account'}
            onClick={() => {
              clearUserMenuCloseTimer()
              setUserMenuOpen((o) => !o)
            }}
            aria-expanded={userMenuOpen}
            style={{
              width: '100%',
              padding: collapsed ? '8px' : '10px 12px',
              borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid transparent',
              cursor: 'pointer',
              textAlign: collapsed ? 'center' : 'left',
              display: 'flex',
              flexDirection: collapsed ? 'column' : 'column',
              alignItems: collapsed ? 'center' : 'stretch',
            }}
          >
            {collapsed ? (
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-sidebar-hover)',
                  color: 'var(--color-sidebar-text-active)',
                  fontSize: '13px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {initials}
              </div>
            ) : (
              <>
                {sidebarDisplayName ? (
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--color-sidebar-text-active)',
                      marginBottom: '6px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sidebarDisplayName}
                  </div>
                ) : null}
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-sidebar-text)',
                    marginBottom: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {profile?.email}
                </div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--color-sidebar-text-active)' }}>
                  {formatRoleLabel(profile?.role)}
                </div>
              </>
            )}
          </button>

          <UserAccountMenu
            open={userMenuOpen}
            showPersonalLibrary
            showAdminPanel={showAdminPanelMenuItem}
            onProfile={() => {
              clearUserMenuCloseTimer()
              setUserMenuOpen(false)
              navigate('/profile')
            }}
            onPersonalLibrary={() => {
              clearUserMenuCloseTimer()
              setUserMenuOpen(false)
              navigate('/personal-knowledge')
            }}
            onAdminPanel={
              showAdminPanelMenuItem && onAdminPanel
                ? () => {
                    clearUserMenuCloseTimer()
                    setUserMenuOpen(false)
                    onAdminPanel()
                  }
                : undefined
            }
            onLogout={() => {
              clearUserMenuCloseTimer()
              setUserMenuOpen(false)
              void handleLogout()
            }}
          />
        </div>
      </div>
    </div>
  )
}
